import { Badge } from '@repo/ui/components/ui/badge'
import { Button } from '@repo/ui/components/ui/button'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@repo/ui/components/ui/dropdown-menu'
import { Input } from '@repo/ui/components/ui/input'
import { Label } from '@repo/ui/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@repo/ui/components/ui/select'
import { Switch } from '@repo/ui/components/ui/switch'
import { cn } from '@repo/ui/lib/utils'
import { CalendarClock, CalendarDays, Clock3, Copy, Plus, RadioTower, Trash2 } from 'lucide-react'
import type { CSSProperties } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { toast } from 'sonner'
import { AppTopBarActionGroup, AppTopBarActions } from '@/components/app/top-bar-actions'
import {
  AppWorkspaceMainSupportingSplit,
  AppWorkspacePane,
  AppWorkspaceTabbedPanel,
  WorkspaceInspectorPane,
  WorkspaceMetricStrip,
  WorkspaceNotice,
  WorkspaceStatusRow,
} from '@/components/app/workspace-shell'
import { WorkspaceEmptyState } from '@/components/operator/workspace-empty-state'
import { PremiumPanel } from '@/components/ui/premium-panel'
import { TIMELINE } from '@/lib/design/onboarding'
import type {
  AvailabilitySlotRecord,
  OperatorConfigurationState,
  ScheduleBreakRecord,
  ScheduleOverrideRecord,
  ScheduleRecord,
} from '@/lib/operator-config/types'
import { trpc } from '@/lib/trpc'

const DAYS = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday']
const DAY_SHORT_LABELS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
const WORKSPACE_DAYS = [1, 2, 3, 4, 5, 6, 0]
const NO_SERVICE_AREA_VALUE = '__no_service_area__'
const NO_LOCATION_VALUE = '__no_location__'
const TIMELINE_START_HOUR = 0
const TIMELINE_END_HOUR = 24
const TIMELINE_SLOT_MINUTES = 30
const TIMELINE_SLOT_COUNT = ((TIMELINE_END_HOUR - TIMELINE_START_HOUR) * 60) / TIMELINE_SLOT_MINUTES
const TIMELINE_VISIBLE_HOURS = 12
const TIMELINE_VIEWPORT_WIDTH = '100%'
const TIMELINE_CANVAS_WIDTH_MULTIPLIER =
  (TIMELINE_END_HOUR - TIMELINE_START_HOUR) / TIMELINE_VISIBLE_HOURS
const TIMELINE_WIDTH_STYLE = {
  '--timeline-viewport-width': TIMELINE_VIEWPORT_WIDTH,
  '--timeline-canvas-width': `calc(var(--timeline-viewport-width) * ${TIMELINE_CANVAS_WIDTH_MULTIPLIER})`,
} as CSSProperties

type ScheduleBundle = {
  schedule: ScheduleRecord & {
    operatorId?: string
  }
  slots: AvailabilitySlotRecord[]
  breaks: ScheduleBreakRecord[]
  overrides: ScheduleOverrideRecord[]
}

type WindowDraft = {
  id: string
  dayOfWeek: number
  startTime: string
  endTime: string
  locationId: string
}

type BreakComposer = {
  dayOfWeek: string
  startTime: string
  endTime: string
  label: string
}

type OverrideComposer = {
  date: string
  isAvailable: boolean
  startTime: string
  endTime: string
}

function minutesBetween(startTime: string, endTime: string) {
  const [startHour, startMinute] = startTime.split(':').map(Number)
  const [endHour, endMinute] = endTime.split(':').map(Number)
  return endHour * 60 + endMinute - (startHour * 60 + startMinute)
}

function formatHours(minutes: number) {
  if (minutes <= 0) return '0h'
  const hours = minutes / 60
  return `${hours % 1 === 0 ? hours.toFixed(0) : hours.toFixed(1)}h`
}

function timeToMinutes(value: string) {
  const [hourText, minuteText] = value.split(':')
  return Number(hourText) * 60 + Number(minuteText)
}

function minutesToTime(value: number) {
  const hour = Math.floor(value / 60)
  const minute = value % 60
  return `${String(hour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`
}

function formatCompactTimeLabel(value: string) {
  const [hourText, minuteText] = value.split(':')
  const hour = Number(hourText)
  const minute = Number(minuteText)
  const suffix = hour >= 12 ? 'pm' : 'am'
  const normalizedHour = hour % 12 || 12
  if (minute === 0) return `${normalizedHour}${suffix}`
  return `${normalizedHour}:${String(minute).padStart(2, '0')}${suffix}`
}

function timeToTimelineSlotIndex(value: string) {
  const timelineStart = TIMELINE_START_HOUR * 60
  const timelineEnd = TIMELINE_END_HOUR * 60
  const clampedMinutes = Math.min(Math.max(timeToMinutes(value), timelineStart), timelineEnd)
  return Math.round((clampedMinutes - timelineStart) / TIMELINE_SLOT_MINUTES)
}

function timelineSlotIndexToTime(index: number) {
  const timelineStart = TIMELINE_START_HOUR * 60
  const clampedIndex = Math.min(Math.max(index, 0), TIMELINE_SLOT_COUNT)
  return minutesToTime(timelineStart + clampedIndex * TIMELINE_SLOT_MINUTES)
}

function getTimelineWindowStyle(window: WindowDraft) {
  const startIndex = timeToTimelineSlotIndex(window.startTime)
  const endIndex = timeToTimelineSlotIndex(window.endTime)
  const left = (startIndex / TIMELINE_SLOT_COUNT) * 100
  const width = (Math.max(endIndex - startIndex, 1) / TIMELINE_SLOT_COUNT) * 100

  return {
    left: `${left}%`,
    width: `${width}%`,
  }
}

function getDraftTimelineStyle(startSlot: number, endSlot: number) {
  const leftSlot = Math.min(startSlot, endSlot)
  const rightSlot = Math.max(startSlot, endSlot) + 1
  return {
    left: `${(leftSlot / TIMELINE_SLOT_COUNT) * 100}%`,
    width: `${((rightSlot - leftSlot) / TIMELINE_SLOT_COUNT) * 100}%`,
  }
}

function formatTimelineHourLabel(index: number) {
  return formatCompactTimeLabel(`${String(TIMELINE_START_HOUR + index).padStart(2, '0')}:00`)
}

function createDefaultWindow(dayOfWeek: number): WindowDraft {
  return {
    id: crypto.randomUUID(),
    dayOfWeek,
    startTime: '09:00',
    endTime: '17:00',
    locationId: '',
  }
}

function sortWindows(windows: WindowDraft[]) {
  return [...windows].sort((left, right) => {
    const dayDiff = left.dayOfWeek - right.dayOfWeek
    if (dayDiff !== 0) return dayDiff
    return left.startTime.localeCompare(right.startTime)
  })
}

function normalizeDayWindows(dayWindows: WindowDraft[]) {
  const sorted = [...dayWindows]
    .filter((window) => timeToMinutes(window.endTime) > timeToMinutes(window.startTime))
    .sort((left, right) => left.startTime.localeCompare(right.startTime))

  return sorted.reduce<WindowDraft[]>((result, window) => {
    const previous = result[result.length - 1]
    if (!previous) {
      result.push(window)
      return result
    }

    const previousEnd = timeToMinutes(previous.endTime)
    const currentStart = timeToMinutes(window.startTime)
    const currentEnd = timeToMinutes(window.endTime)

    if (currentStart <= previousEnd && previous.locationId === window.locationId) {
      previous.endTime = minutesToTime(Math.max(previousEnd, currentEnd))
      return result
    }

    result.push(window)
    return result
  }, [])
}

export function HoursWorkspace({
  configuration,
  onSaved,
}: {
  configuration: OperatorConfigurationState
  onSaved?: () => Promise<void> | void
}) {
  const utils = trpc.useUtils()
  const scheduleListQuery = trpc.schedule.list.useQuery(undefined, {
    enabled: Boolean(configuration.operator),
  })
  const createScheduleMutation = trpc.schedule.create.useMutation()
  const updateScheduleMutation = trpc.schedule.update.useMutation()
  const deleteScheduleMutation = trpc.schedule.delete.useMutation()
  const setScheduleMutation = trpc.schedule.set.useMutation()
  const upsertBreakMutation = trpc.schedule.breaks.upsert.useMutation()
  const deleteBreakMutation = trpc.schedule.breaks.delete.useMutation()
  const upsertOverrideMutation = trpc.schedule.overrides.upsert.useMutation()
  const deleteOverrideMutation = trpc.schedule.overrides.delete.useMutation()

  const operatorTimezone = configuration.operator?.timezone ?? 'America/Los_Angeles'
  const scheduleBundles = useMemo(
    () => ((scheduleListQuery.data ?? []) as ScheduleBundle[]).map((bundle) => bundle),
    [scheduleListQuery.data]
  )

  const [selectedScheduleId, setSelectedScheduleId] = useState<string | null>(null)
  const [draftName, setDraftName] = useState('')
  const [draftTimezone, setDraftTimezone] = useState(operatorTimezone)
  const [draftServiceAreaId, setDraftServiceAreaId] = useState('')
  const [draftWindows, setDraftWindows] = useState<WindowDraft[]>([])
  const [breakComposer, setBreakComposer] = useState<BreakComposer>({
    dayOfWeek: '1',
    startTime: '12:00',
    endTime: '12:30',
    label: '',
  })
  const [overrideComposer, setOverrideComposer] = useState<OverrideComposer>({
    date: '',
    isAvailable: true,
    startTime: '09:00',
    endTime: '17:00',
  })
  const [activeComposer, setActiveComposer] = useState<'break' | 'exception' | null>(null)
  const [dragSelection, setDragSelection] = useState<{
    dayOfWeek: number
    startSlot: number
    endSlot: number
  } | null>(null)
  const [selectedWindowId, setSelectedWindowId] = useState<string | null>(null)
  const timelineScrollRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const timelineScrollInitializedRef = useRef<Record<number, boolean>>({})

  useEffect(() => {
    if (scheduleBundles.length === 0) {
      setSelectedScheduleId(null)
      return
    }

    const activeExists = scheduleBundles.some((bundle) => bundle.schedule.id === selectedScheduleId)
    if (!activeExists) {
      setSelectedScheduleId(scheduleBundles[0]?.schedule.id ?? null)
    }
  }, [scheduleBundles, selectedScheduleId])

  const selectedBundle = useMemo(
    () =>
      scheduleBundles.find((bundle) => bundle.schedule.id === selectedScheduleId) ??
      scheduleBundles[0] ??
      null,
    [scheduleBundles, selectedScheduleId]
  )

  useEffect(() => {
    if (!selectedBundle) return

    setDraftName(selectedBundle.schedule.name)
    setDraftTimezone(selectedBundle.schedule.timezone ?? operatorTimezone)
    setDraftServiceAreaId(
      selectedBundle.schedule.serviceAreaId ?? configuration.serviceArea?.id ?? ''
    )
    setDraftWindows(
      sortWindows(
        selectedBundle.slots.map((slot) => ({
          id: slot.id,
          dayOfWeek: slot.dayOfWeek,
          startTime: slot.startTime,
          endTime: slot.endTime,
          locationId: slot.locationId ?? '',
        }))
      )
    )
    setSelectedWindowId(null)
    timelineScrollInitializedRef.current = {}
  }, [configuration.serviceArea?.id, operatorTimezone, selectedBundle])

  const publishedBundle = useMemo(
    () => scheduleBundles.find((bundle) => bundle.schedule.isDefault) ?? null,
    [scheduleBundles]
  )

  const weeklyMinutes = useMemo(
    () =>
      draftWindows.reduce(
        (total, slot) => total + Math.max(0, minutesBetween(slot.startTime, slot.endTime)),
        0
      ),
    [draftWindows]
  )

  const windowsByDay = useMemo(() => {
    return DAYS.map((_, dayOfWeek) =>
      draftWindows
        .filter((slot) => slot.dayOfWeek === dayOfWeek)
        .sort((a, b) => a.startTime.localeCompare(b.startTime))
    )
  }, [draftWindows])

  const selectedServiceArea = useMemo(
    () =>
      configuration.serviceAreaPolicies.find((policy) => policy.id === draftServiceAreaId) ?? null,
    [configuration.serviceAreaPolicies, draftServiceAreaId]
  )

  const selectedWindow = useMemo(
    () => draftWindows.find((window) => window.id === selectedWindowId) ?? null,
    [draftWindows, selectedWindowId]
  )

  useEffect(() => {
    if (selectedWindowId && !selectedWindow) {
      setSelectedWindowId(null)
    }
  }, [selectedWindow, selectedWindowId])

  useEffect(() => {
    if (!selectedScheduleId) return

    WORKSPACE_DAYS.forEach((dayOfWeek) => {
      const container = timelineScrollRefs.current[dayOfWeek]
      if (!container || timelineScrollInitializedRef.current[dayOfWeek]) return

      const maxScroll = container.scrollWidth - container.clientWidth
      const defaultOffset =
        ((7.5 - TIMELINE_START_HOUR) / (TIMELINE_END_HOUR - TIMELINE_START_HOUR)) *
        container.scrollWidth
      container.scrollLeft = Math.min(maxScroll, Math.max(0, defaultOffset))
      timelineScrollInitializedRef.current[dayOfWeek] = true
    })
  }, [selectedScheduleId])

  async function refreshWorkspace() {
    await Promise.all([
      utils.schedule.list.invalidate(),
      utils.schedule.get.invalidate(),
      onSaved?.(),
    ])
  }

  function updateWindow(windowId: string, patch: Partial<WindowDraft>) {
    setDraftWindows((current) =>
      sortWindows(
        current.map((window) => (window.id === windowId ? { ...window, ...patch } : window))
      )
    )
  }

  function removeWindow(windowId: string) {
    setDraftWindows((current) => current.filter((window) => window.id !== windowId))
  }

  function setDayEnabled(dayOfWeek: number, enabled: boolean) {
    setDraftWindows((current) => {
      const existing = current.filter((window) => window.dayOfWeek === dayOfWeek)
      if (!enabled) return current.filter((window) => window.dayOfWeek !== dayOfWeek)
      if (existing.length > 0) return current
      return sortWindows([...current, createDefaultWindow(dayOfWeek)])
    })
  }

  const commitDragSelection = useCallback(() => {
    if (!dragSelection) return

    const dayOfWeek = dragSelection.dayOfWeek
    const startSlot = Math.min(dragSelection.startSlot, dragSelection.endSlot)
    const endSlot = Math.max(dragSelection.startSlot, dragSelection.endSlot) + 1
    const nextWindowId = crypto.randomUUID()
    const nextWindow: WindowDraft = {
      id: nextWindowId,
      dayOfWeek,
      startTime: timelineSlotIndexToTime(startSlot),
      endTime: timelineSlotIndexToTime(endSlot),
      locationId: '',
    }

    setDraftWindows((current) => {
      const otherDays = current.filter((window) => window.dayOfWeek !== dayOfWeek)
      const sameDay = current.filter((window) => window.dayOfWeek === dayOfWeek)
      return sortWindows([...otherDays, ...normalizeDayWindows([...sameDay, nextWindow])])
    })
    setSelectedWindowId(nextWindowId)
    setDragSelection(null)
  }, [dragSelection])

  useEffect(() => {
    if (!dragSelection) return

    function handleMouseUp() {
      commitDragSelection()
    }

    window.addEventListener('mouseup', handleMouseUp)
    return () => window.removeEventListener('mouseup', handleMouseUp)
  }, [commitDragSelection, dragSelection])

  function startTimelineDrag(dayOfWeek: number, slotIndex: number) {
    setDragSelection({ dayOfWeek, startSlot: slotIndex, endSlot: slotIndex })
  }

  function updateTimelineDrag(dayOfWeek: number, slotIndex: number) {
    setDragSelection((current) => {
      if (!current || current.dayOfWeek !== dayOfWeek) return current
      return { ...current, endSlot: slotIndex }
    })
  }

  function handleTimelineAutoScroll(dayOfWeek: number, event: { clientX: number }) {
    if (!dragSelection || dragSelection.dayOfWeek !== dayOfWeek) return

    const container = timelineScrollRefs.current[dayOfWeek]
    if (!container) return

    const rect = container.getBoundingClientRect()
    const threshold = 64
    const step = 28

    if (event.clientX < rect.left + threshold) {
      container.scrollLeft = Math.max(0, container.scrollLeft - step)
      return
    }

    if (event.clientX > rect.right - threshold) {
      container.scrollLeft = Math.min(
        container.scrollWidth - container.clientWidth,
        container.scrollLeft + step
      )
    }
  }

  function copyDayToVisibleDays(dayOfWeek: number) {
    const sourceWindows = windowsByDay[dayOfWeek] ?? []
    if (sourceWindows.length === 0) {
      toast.error(`Set ${DAYS[dayOfWeek]} first, then copy it.`)
      return
    }

    setDraftWindows((current) => {
      const untouched = current.filter((window) => !WORKSPACE_DAYS.includes(window.dayOfWeek))
      const copied = WORKSPACE_DAYS.flatMap((targetDay) =>
        sourceWindows.map((window) => ({
          ...window,
          id: crypto.randomUUID(),
          dayOfWeek: targetDay,
        }))
      )

      return sortWindows([...untouched, ...copied])
    })
  }

  function copyMondayAcrossWeekdays() {
    const monday = windowsByDay[1] ?? []

    if (monday.length === 0) {
      toast.error('Set Monday first, then copy it across the week.')
      return
    }

    const replicated = monday.map((window) => ({
      ...window,
      id: crypto.randomUUID(),
    }))

    setDraftWindows((current) => {
      const nonWeekdays = current.filter(
        (window) => window.dayOfWeek === 0 || window.dayOfWeek === 6
      )
      const weekdays = [1, 2, 3, 4, 5].flatMap((dayOfWeek) =>
        replicated.map((window) => ({
          ...window,
          id: crypto.randomUUID(),
          dayOfWeek,
        }))
      )

      return sortWindows([...nonWeekdays, ...weekdays])
    })
  }

  async function handleCreateSchedule() {
    const nextNumber = scheduleBundles.length + 1

    try {
      const created = await createScheduleMutation.mutateAsync({
        name: `Schedule ${nextNumber}`,
        timezone: operatorTimezone,
      })
      setSelectedScheduleId(created.id)
      await refreshWorkspace()
      toast.success('New schedule added.')
    } catch (error) {
      toast.error((error as Error).message || 'Unable to create schedule.')
    }
  }

  async function handleDuplicateSchedule(sourceBundle: ScheduleBundle) {
    const nextNumber = scheduleBundles.length + 1
    const copyName = `${sourceBundle.schedule.name || `Schedule ${nextNumber}`} Copy`

    try {
      const created = await createScheduleMutation.mutateAsync({
        name: copyName,
        timezone: sourceBundle.schedule.timezone ?? operatorTimezone,
      })

      await setScheduleMutation.mutateAsync({
        scheduleId: created.id,
        name: copyName,
        timezone: sourceBundle.schedule.timezone ?? operatorTimezone,
        serviceAreaId: sourceBundle.schedule.serviceAreaId ?? null,
        slots: sourceBundle.slots.map((slot) => ({
          dayOfWeek: slot.dayOfWeek,
          startTime: slot.startTime,
          endTime: slot.endTime,
          locationId: slot.locationId ?? null,
        })),
      })

      await Promise.all([
        ...sourceBundle.breaks.map((scheduleBreak) =>
          upsertBreakMutation.mutateAsync({
            scheduleId: created.id,
            dayOfWeek: scheduleBreak.dayOfWeek,
            startTime: scheduleBreak.startTime,
            endTime: scheduleBreak.endTime,
            label: scheduleBreak.label ?? undefined,
          })
        ),
        ...sourceBundle.overrides.map((override) =>
          upsertOverrideMutation.mutateAsync({
            scheduleId: created.id,
            date: override.date,
            isAvailable: override.isAvailable,
            startTime: override.startTime ?? undefined,
            endTime: override.endTime ?? undefined,
          })
        ),
      ])

      setSelectedScheduleId(created.id)
      await refreshWorkspace()
      toast.success('Schedule duplicated.')
    } catch (error) {
      toast.error((error as Error).message || 'Unable to duplicate schedule.')
    }
  }

  async function handleDeleteSelectedSchedule() {
    if (!selectedBundle) return
    if (scheduleBundles.length <= 1) {
      toast.error('Keep at least one schedule in the workspace.')
      return
    }

    try {
      await deleteScheduleMutation.mutateAsync({ id: selectedBundle.schedule.id })
      await refreshWorkspace()
      toast.success('Schedule removed.')
    } catch (error) {
      toast.error((error as Error).message || 'Unable to delete schedule.')
    }
  }

  async function handlePublishSelectedSchedule() {
    if (!selectedBundle) return

    const cleanName = draftName.trim()
    if (!cleanName) {
      toast.error('Give this schedule a name before publishing.')
      return
    }

    const hasInvalidWindow = draftWindows.some(
      (window) => minutesBetween(window.startTime, window.endTime) <= 0
    )

    if (hasInvalidWindow) {
      toast.error('Every operating window needs an end time after its start time.')
      return
    }

    try {
      await setScheduleMutation.mutateAsync({
        scheduleId: selectedBundle.schedule.id,
        name: cleanName,
        timezone: draftTimezone.trim() || operatorTimezone,
        serviceAreaId: draftServiceAreaId || null,
        slots: draftWindows.map((window) => ({
          dayOfWeek: window.dayOfWeek,
          startTime: window.startTime,
          endTime: window.endTime,
          locationId: window.locationId || null,
        })),
      })
      await updateScheduleMutation.mutateAsync({
        id: selectedBundle.schedule.id,
        name: cleanName,
        timezone: draftTimezone.trim() || operatorTimezone,
        isDefault: true,
      })
      await refreshWorkspace()
      toast.success(`${cleanName} is now live.`)
    } catch (error) {
      toast.error((error as Error).message || 'Unable to publish this schedule.')
    }
  }

  async function handleSaveSchedule() {
    if (!selectedBundle) return

    const cleanName = draftName.trim()
    if (!cleanName) {
      toast.error('Give this schedule a name before saving.')
      return
    }

    const hasInvalidWindow = draftWindows.some(
      (window) => minutesBetween(window.startTime, window.endTime) <= 0
    )

    if (hasInvalidWindow) {
      toast.error('Every operating window needs an end time after its start time.')
      return
    }

    try {
      await updateScheduleMutation.mutateAsync({
        id: selectedBundle.schedule.id,
        name: cleanName,
        timezone: draftTimezone.trim() || operatorTimezone,
      })

      await setScheduleMutation.mutateAsync({
        scheduleId: selectedBundle.schedule.id,
        name: cleanName,
        timezone: draftTimezone.trim() || operatorTimezone,
        serviceAreaId: draftServiceAreaId || null,
        slots: draftWindows.map((window) => ({
          dayOfWeek: window.dayOfWeek,
          startTime: window.startTime,
          endTime: window.endTime,
          locationId: window.locationId || null,
        })),
      })

      await refreshWorkspace()
      toast.success('Schedule blueprint saved.')
    } catch (error) {
      toast.error((error as Error).message || 'Unable to save this schedule.')
    }
  }

  async function handleAddBreak() {
    if (!selectedBundle) return
    if (minutesBetween(breakComposer.startTime, breakComposer.endTime) <= 0) {
      toast.error('Break end time must be after the start time.')
      return
    }

    try {
      await upsertBreakMutation.mutateAsync({
        scheduleId: selectedBundle.schedule.id,
        dayOfWeek: Number(breakComposer.dayOfWeek),
        startTime: breakComposer.startTime,
        endTime: breakComposer.endTime,
        label: breakComposer.label || undefined,
      })
      setBreakComposer({
        dayOfWeek: breakComposer.dayOfWeek,
        startTime: '12:00',
        endTime: '12:30',
        label: '',
      })
      await refreshWorkspace()
      toast.success('Break added.')
    } catch (error) {
      toast.error((error as Error).message || 'Unable to add break.')
    }
  }

  async function handleDeleteBreak(breakId: string) {
    try {
      await deleteBreakMutation.mutateAsync({ id: breakId })
      await refreshWorkspace()
      toast.success('Break removed.')
    } catch (error) {
      toast.error((error as Error).message || 'Unable to remove break.')
    }
  }

  async function handleSaveOverride() {
    if (!selectedBundle) return
    if (!overrideComposer.date) {
      toast.error('Choose a date for this schedule exception.')
      return
    }
    if (
      overrideComposer.isAvailable &&
      minutesBetween(overrideComposer.startTime, overrideComposer.endTime) <= 0
    ) {
      toast.error('Exception hours need a valid time range.')
      return
    }

    try {
      await upsertOverrideMutation.mutateAsync({
        scheduleId: selectedBundle.schedule.id,
        date: overrideComposer.date,
        isAvailable: overrideComposer.isAvailable,
        startTime: overrideComposer.isAvailable ? overrideComposer.startTime : undefined,
        endTime: overrideComposer.isAvailable ? overrideComposer.endTime : undefined,
      })
      setOverrideComposer({
        date: '',
        isAvailable: true,
        startTime: '09:00',
        endTime: '17:00',
      })
      await refreshWorkspace()
      toast.success('Schedule exception saved.')
    } catch (error) {
      toast.error((error as Error).message || 'Unable to save the exception date.')
    }
  }

  async function handleDeleteOverride(overrideId: string) {
    try {
      await deleteOverrideMutation.mutateAsync({ id: overrideId })
      await refreshWorkspace()
      toast.success('Exception removed.')
    } catch (error) {
      toast.error((error as Error).message || 'Unable to remove the exception.')
    }
  }

  const scheduleCreateMenu = (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button
          variant="ghost"
          size="icon-sm"
          className="h-[3.25rem] w-10 shrink-0 cursor-pointer rounded-t-[5px] text-muted-foreground hover:bg-muted/30 hover:text-foreground"
          aria-label="Create alternate schedule"
          disabled={createScheduleMutation.isPending}
        >
          <Plus className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="w-64">
        <DropdownMenuLabel className="text-xs text-muted-foreground">
          New schedule
        </DropdownMenuLabel>
        <DropdownMenuItem className="cursor-pointer" onClick={handleCreateSchedule}>
          <Plus className="h-4 w-4" />
          Blank schedule
        </DropdownMenuItem>
        {scheduleBundles.length > 0 ? (
          <>
            <DropdownMenuSeparator />
            <DropdownMenuLabel className="text-xs text-muted-foreground">
              Duplicate from
            </DropdownMenuLabel>
            {scheduleBundles.map((bundle) => (
              <DropdownMenuItem
                key={bundle.schedule.id}
                className="cursor-pointer"
                onClick={() => handleDuplicateSchedule(bundle)}
              >
                <Copy className="h-4 w-4" />
                <span className="truncate">{bundle.schedule.name}</span>
              </DropdownMenuItem>
            ))}
          </>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  )

  if (!selectedBundle) {
    return (
      <div className="space-y-5">
        <AppTopBarActions>
          <AppTopBarActionGroup>
            <Button className="cursor-pointer gap-2" size="sm" onClick={handleCreateSchedule}>
              <Plus className="h-4 w-4" />
              Create alternate
            </Button>
          </AppTopBarActionGroup>
        </AppTopBarActions>
        <PremiumPanel className="rounded-[6px] border-border/70">
          <WorkspaceEmptyState
            badgeIcon={<CalendarClock className="h-6 w-6 text-primary" />}
            actionIcon={<Plus className="h-4 w-4" />}
            title="Build your first hours blueprint."
            description="Create a named schedule, then define the area it covers, the operating windows you want open, and the breaks that should block time."
            primaryActionLabel="Add schedule"
            onPrimaryAction={handleCreateSchedule}
            className="min-h-[24rem]"
          />
        </PremiumPanel>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      <AppTopBarActions>
        <AppTopBarActionGroup>
          {!selectedBundle.schedule.isDefault ? (
            <Button
              variant="outline"
              size="sm"
              className="cursor-pointer gap-2"
              onClick={handlePublishSelectedSchedule}
              disabled={updateScheduleMutation.isPending}
            >
              <RadioTower className="h-4 w-4" />
              Publish alternate
            </Button>
          ) : null}
          <Button
            size="sm"
            className="cursor-pointer gap-2"
            onClick={handleSaveSchedule}
            disabled={setScheduleMutation.isPending || updateScheduleMutation.isPending}
          >
            Save
          </Button>
        </AppTopBarActionGroup>
      </AppTopBarActions>

      <AppWorkspaceTabbedPanel
        emptyLabel="No schedules yet"
        tabs={scheduleBundles.map((bundle) => {
          const isSelected = bundle.schedule.id === selectedBundle.schedule.id

          return {
            key: bundle.schedule.id,
            active: isSelected,
            children: (
              <>
                {isSelected ? (
                  <Input
                    value={draftName}
                    onChange={(event) => setDraftName(event.target.value)}
                    aria-label="Schedule name"
                    className="h-8 min-w-0 flex-1 border-transparent bg-transparent px-0 py-0 font-medium leading-none shadow-none focus-visible:border-border focus-visible:bg-background"
                  />
                ) : (
                  <button
                    type="button"
                    className="flex h-8 min-w-0 flex-1 cursor-pointer items-center text-left outline-none focus-visible:ring-2 focus-visible:ring-ring/40"
                    onClick={() => setSelectedScheduleId(bundle.schedule.id)}
                  >
                    <div className="truncate text-sm font-medium">{bundle.schedule.name}</div>
                  </button>
                )}
                {bundle.schedule.isDefault ? (
                  <span
                    className="h-2.5 w-2.5 shrink-0 rounded-full border border-success/30 bg-success/70 shadow-[0_0_0_3px_color-mix(in_oklab,var(--success)_10%,transparent)]"
                    title="Live"
                  >
                    <span className="sr-only">Live</span>
                  </span>
                ) : null}
              </>
            ),
          }
        })}
        addControl={scheduleCreateMenu}
      >
        <AppWorkspaceMainSupportingSplit>
          <AppWorkspacePane padded scroll>
            <div className="flex flex-wrap items-center justify-between gap-3">
              <div>
                <div className="app-section-label">Weekly hours</div>
                <div className="mt-1 text-sm text-muted-foreground">
                  Drag across a day to create bookable hours. Select a window to set its location.
                </div>
              </div>
              <div className="flex flex-wrap gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer gap-2"
                  onClick={copyMondayAcrossWeekdays}
                >
                  <Copy className="h-4 w-4" />
                  Copy Monday
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="cursor-pointer"
                  onClick={() =>
                    setDraftWindows(
                      [1, 2, 3, 4, 5].map((dayOfWeek) => createDefaultWindow(dayOfWeek))
                    )
                  }
                >
                  Weekdays 9-5
                </Button>
              </div>
            </div>

            <div className="mt-5 space-y-3">
              {WORKSPACE_DAYS.map((dayOfWeek) => {
                const dayWindows = windowsByDay[dayOfWeek] ?? []
                const dayActive = dayWindows.length > 0
                const draftStyle =
                  dragSelection && dragSelection.dayOfWeek === dayOfWeek
                    ? getDraftTimelineStyle(dragSelection.startSlot, dragSelection.endSlot)
                    : null

                return (
                  <div
                    key={dayOfWeek}
                    className={cn(
                      'rounded-[6px] border border-border/70 bg-background/55 px-3 py-3',
                      !dayActive && 'bg-muted/15'
                    )}
                  >
                    <div className="grid gap-3 lg:grid-cols-[7rem_minmax(0,1fr)_auto] lg:items-center">
                      <div className="flex items-center gap-3">
                        <Switch
                          checked={dayActive}
                          onCheckedChange={(checked) => setDayEnabled(dayOfWeek, checked)}
                          aria-label={`Enable ${DAYS[dayOfWeek]}`}
                        />
                        <div>
                          <div className="text-sm font-semibold">{DAY_SHORT_LABELS[dayOfWeek]}</div>
                          <div className="text-xs text-muted-foreground">
                            {dayWindows.length === 0
                              ? 'Closed'
                              : dayWindows.length === 1
                                ? `${formatCompactTimeLabel(dayWindows[0].startTime)}-${formatCompactTimeLabel(dayWindows[0].endTime)}`
                                : `${dayWindows.length} windows`}
                          </div>
                        </div>
                      </div>

                      <div className={cn(TIMELINE.body, dayActive ? 'opacity-100' : 'opacity-45')}>
                        <div
                          ref={(node) => {
                            timelineScrollRefs.current[dayOfWeek] = node
                          }}
                          style={TIMELINE_WIDTH_STYLE}
                          role="application"
                          onMouseMove={(event) => handleTimelineAutoScroll(dayOfWeek, event)}
                          className={cn(TIMELINE.viewport, 'rounded-[4px] p-1')}
                        >
                          <div className={cn(TIMELINE.canvas, 'h-[2.75rem]')}>
                            <div className={TIMELINE.hourRail}>
                              {Array.from(
                                { length: TIMELINE_END_HOUR - TIMELINE_START_HOUR + 1 },
                                (_, hourIndex) => {
                                  if (
                                    hourIndex !== 0 &&
                                    hourIndex !== TIMELINE_END_HOUR - TIMELINE_START_HOUR &&
                                    hourIndex % 2 !== 0
                                  ) {
                                    return null
                                  }

                                  const left =
                                    (hourIndex / (TIMELINE_END_HOUR - TIMELINE_START_HOUR)) * 100
                                  return (
                                    <span
                                      key={`${DAYS[dayOfWeek]}-hour-${hourIndex}`}
                                      className={cn(
                                        TIMELINE.hourLabel,
                                        hourIndex === 0
                                          ? 'translate-x-0'
                                          : hourIndex === TIMELINE_END_HOUR - TIMELINE_START_HOUR
                                            ? '-translate-x-full'
                                            : '-translate-x-1/2'
                                      )}
                                      style={{ left: `${left}%` }}
                                    >
                                      {formatTimelineHourLabel(hourIndex)}
                                    </span>
                                  )
                                }
                              )}
                            </div>

                            <div className={TIMELINE.slotLayer}>
                              {Array.from({ length: TIMELINE_SLOT_COUNT }, (_, slotIndex) => (
                                <button
                                  key={`${DAYS[dayOfWeek]}-slot-${slotIndex}`}
                                  type="button"
                                  disabled={!dayActive}
                                  aria-label={`Drag from ${formatCompactTimeLabel(timelineSlotIndexToTime(slotIndex))} to add ${DAYS[dayOfWeek]} hours`}
                                  title={`Drag from ${formatCompactTimeLabel(timelineSlotIndexToTime(slotIndex))} to add hours`}
                                  className={cn(
                                    TIMELINE.slotButton,
                                    slotIndex % 2 === 0 ? TIMELINE.slotStripe : 'bg-transparent',
                                    dayActive ? TIMELINE.slotHover : ''
                                  )}
                                  style={{
                                    left: `${(slotIndex / TIMELINE_SLOT_COUNT) * 100}%`,
                                    width: `${(1 / TIMELINE_SLOT_COUNT) * 100}%`,
                                  }}
                                  onMouseDown={() => startTimelineDrag(dayOfWeek, slotIndex)}
                                  onMouseEnter={() => updateTimelineDrag(dayOfWeek, slotIndex)}
                                  onMouseUp={commitDragSelection}
                                />
                              ))}

                              {dayWindows.map((window) => (
                                <button
                                  key={window.id}
                                  type="button"
                                  title={`Select ${formatCompactTimeLabel(window.startTime)}-${formatCompactTimeLabel(window.endTime)}`}
                                  className={cn(
                                    TIMELINE.window,
                                    selectedWindowId === window.id
                                      ? 'ring-2 ring-primary/35 ring-offset-1 ring-offset-background'
                                      : ''
                                  )}
                                  style={getTimelineWindowStyle(window)}
                                  onClick={() => setSelectedWindowId(window.id)}
                                >
                                  <span className="line-clamp-2 block">
                                    {formatCompactTimeLabel(window.startTime)}-
                                    {formatCompactTimeLabel(window.endTime)}
                                  </span>
                                </button>
                              ))}

                              {draftStyle ? (
                                <div className={TIMELINE.draftWindow} style={draftStyle} />
                              ) : null}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="cursor-pointer"
                          onClick={() => copyDayToVisibleDays(dayOfWeek)}
                          aria-label={`Copy ${DAYS[dayOfWeek]} to visible days`}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>

                    {selectedWindow?.dayOfWeek === dayOfWeek ? (
                      <div className="mt-3 grid gap-2 rounded-[4px] border border-primary/25 bg-primary/[0.035] p-2 md:grid-cols-[minmax(9rem,13rem)_minmax(0,1fr)_auto] md:items-center">
                        <div className="px-2 text-sm">
                          <div className="font-medium text-foreground">
                            {formatCompactTimeLabel(selectedWindow.startTime)}-
                            {formatCompactTimeLabel(selectedWindow.endTime)}
                          </div>
                          <div className="text-xs text-muted-foreground">Selected window</div>
                        </div>
                        <Select
                          value={selectedWindow.locationId || NO_LOCATION_VALUE}
                          onValueChange={(value) =>
                            updateWindow(selectedWindow.id, {
                              locationId: value === NO_LOCATION_VALUE ? '' : value,
                            })
                          }
                        >
                          <SelectTrigger
                            className="h-10 w-full rounded-[4px] border-border/70 bg-background px-3 shadow-none"
                            aria-label={`${DAYS[dayOfWeek]} selected window location tag`}
                          >
                            <SelectValue />
                          </SelectTrigger>
                          <SelectContent className="rounded-[4px]">
                            <SelectItem value={NO_LOCATION_VALUE}>No fixed location</SelectItem>
                            {configuration.locations.map((location) => (
                              <SelectItem key={location.id} value={location.id}>
                                {location.label}
                              </SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="cursor-pointer justify-start gap-2 text-destructive md:justify-center"
                          onClick={() => removeWindow(selectedWindow.id)}
                        >
                          <Trash2 className="h-4 w-4" />
                          <span className="md:hidden">Delete window</span>
                        </Button>
                      </div>
                    ) : null}
                  </div>
                )
              })}
            </div>
          </AppWorkspacePane>

          <WorkspaceInspectorPane className="p-5 md:p-5">
            <div className="app-section-label">Details</div>

            <div className="mt-5 space-y-5">
              <section className="space-y-3">
                <div className="flex items-center justify-between gap-3">
                  <div className="text-sm font-semibold">Schedule</div>
                  {selectedBundle.schedule.isDefault ? (
                    <Badge
                      variant="outline"
                      className="rounded-[4px] border-primary/40 bg-primary/10"
                    >
                      Live
                    </Badge>
                  ) : (
                    <Badge variant="outline" className="rounded-[4px]">
                      Alternate
                    </Badge>
                  )}
                </div>
                <WorkspaceMetricStrip
                  className="grid-cols-3"
                  metrics={[
                    { key: 'weekly', label: 'weekly', value: formatHours(weeklyMinutes) },
                    { key: 'windows', label: 'windows', value: draftWindows.length },
                    {
                      key: 'tagged',
                      label: 'tagged',
                      value: draftWindows.filter((slot) => slot.locationId).length,
                    },
                  ]}
                />
                <div className="space-y-2">
                  <WorkspaceStatusRow
                    label="Booking page"
                    value={publishedBundle ? 'Uses live schedule' : 'No live schedule'}
                  />
                  <WorkspaceStatusRow
                    label="Service area"
                    value={selectedServiceArea?.name ?? 'Not assigned'}
                  />
                  <WorkspaceStatusRow label="Timezone" value={draftTimezone || operatorTimezone} />
                </div>
                <div className="space-y-3">
                  <div className="space-y-2">
                    <Label htmlFor="service-area">Service area</Label>
                    <Select
                      value={draftServiceAreaId || NO_SERVICE_AREA_VALUE}
                      onValueChange={(value) =>
                        setDraftServiceAreaId(value === NO_SERVICE_AREA_VALUE ? '' : value)
                      }
                    >
                      <SelectTrigger
                        id="service-area"
                        className="h-10 w-full rounded-[4px] border-border/70 bg-background px-3 shadow-none"
                      >
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-[4px]">
                        <SelectItem value={NO_SERVICE_AREA_VALUE}>Choose a saved area</SelectItem>
                        {configuration.serviceAreaPolicies.map((policy) => (
                          <SelectItem key={policy.id} value={policy.id}>
                            {policy.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <Button
                    variant="ghost"
                    className="w-full cursor-pointer justify-start gap-2 text-destructive"
                    onClick={handleDeleteSelectedSchedule}
                    disabled={scheduleBundles.length <= 1 || deleteScheduleMutation.isPending}
                  >
                    <Trash2 className="h-4 w-4" />
                    Delete schedule
                  </Button>
                </div>
              </section>

              <section className="border-t border-border/70 pt-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Clock3 className="h-4 w-4 text-primary" />
                    Breaks
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 cursor-pointer"
                    onClick={() => setActiveComposer(activeComposer === 'break' ? null : 'break')}
                  >
                    Add
                  </Button>
                </div>
                <div className="mt-3 space-y-2">
                  {selectedBundle.breaks.length === 0 ? (
                    <WorkspaceNotice className="border-dashed">
                      No recurring breaks.
                    </WorkspaceNotice>
                  ) : (
                    selectedBundle.breaks.map((breakRow) => (
                      <div
                        key={breakRow.id}
                        className="flex items-start justify-between gap-3 rounded-[4px] border border-border/70 bg-background/55 px-3 py-2"
                      >
                        <div className="min-w-0 text-sm">
                          <div className="font-medium">
                            {DAY_SHORT_LABELS[breakRow.dayOfWeek]} · {breakRow.startTime}-
                            {breakRow.endTime}
                          </div>
                          <div className="truncate text-muted-foreground">
                            {breakRow.label || 'Break'}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="cursor-pointer"
                          onClick={() => handleDeleteBreak(breakRow.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                {activeComposer === 'break' ? (
                  <div className="mt-3 grid gap-2 rounded-[6px] border border-border/70 bg-muted/20 p-3">
                    <Select
                      value={breakComposer.dayOfWeek}
                      onValueChange={(value) =>
                        setBreakComposer((current) => ({
                          ...current,
                          dayOfWeek: value,
                        }))
                      }
                    >
                      <SelectTrigger className="h-10 w-full rounded-[4px] border-border/70 bg-background px-3 shadow-none">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="rounded-[4px]">
                        {DAYS.map((day, index) => (
                          <SelectItem key={day} value={String(index)}>
                            {day}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Input
                      value={breakComposer.label}
                      onChange={(event) =>
                        setBreakComposer((current) => ({ ...current, label: event.target.value }))
                      }
                      placeholder="School pickup"
                    />
                    <div className="grid grid-cols-2 gap-2">
                      <Input
                        type="time"
                        value={breakComposer.startTime}
                        onChange={(event) =>
                          setBreakComposer((current) => ({
                            ...current,
                            startTime: event.target.value,
                          }))
                        }
                      />
                      <Input
                        type="time"
                        value={breakComposer.endTime}
                        onChange={(event) =>
                          setBreakComposer((current) => ({
                            ...current,
                            endTime: event.target.value,
                          }))
                        }
                      />
                    </div>
                    <Button
                      className="cursor-pointer"
                      onClick={handleAddBreak}
                      disabled={upsertBreakMutation.isPending}
                    >
                      Add break
                    </Button>
                  </div>
                ) : null}
              </section>

              <section className="border-t border-border/70 pt-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <CalendarDays className="h-4 w-4 text-primary" />
                    Exceptions
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    className="h-8 cursor-pointer"
                    onClick={() =>
                      setActiveComposer(activeComposer === 'exception' ? null : 'exception')
                    }
                  >
                    Add
                  </Button>
                </div>
                <div className="mt-3 space-y-2">
                  {selectedBundle.overrides.length === 0 ? (
                    <WorkspaceNotice className="border-dashed">No exception dates.</WorkspaceNotice>
                  ) : (
                    selectedBundle.overrides.map((override) => (
                      <div
                        key={override.id}
                        className="flex items-start justify-between gap-3 rounded-[4px] border border-border/70 bg-background/55 px-3 py-2"
                      >
                        <div className="text-sm">
                          <div className="font-medium">{override.date}</div>
                          <div className="text-muted-foreground">
                            {override.isAvailable
                              ? `${override.startTime || '--'}-${override.endTime || '--'}`
                              : 'Closed all day'}
                          </div>
                        </div>
                        <Button
                          variant="ghost"
                          size="icon-sm"
                          className="cursor-pointer"
                          onClick={() => handleDeleteOverride(override.id)}
                        >
                          <Trash2 className="h-4 w-4 text-destructive" />
                        </Button>
                      </div>
                    ))
                  )}
                </div>

                {activeComposer === 'exception' ? (
                  <div className="mt-3 grid gap-2 rounded-[6px] border border-border/70 bg-muted/20 p-3">
                    <Input
                      type="date"
                      value={overrideComposer.date}
                      onChange={(event) =>
                        setOverrideComposer((current) => ({
                          ...current,
                          date: event.target.value,
                        }))
                      }
                    />
                    <label className="flex cursor-pointer items-center gap-3 rounded-[4px] border border-border/70 bg-background/70 px-3 py-2">
                      <input
                        type="checkbox"
                        checked={overrideComposer.isAvailable}
                        onChange={(event) =>
                          setOverrideComposer((current) => ({
                            ...current,
                            isAvailable: event.target.checked,
                          }))
                        }
                        className="h-4 w-4 cursor-pointer rounded border-border"
                      />
                      <span className="text-sm">Available on this date</span>
                    </label>
                    {overrideComposer.isAvailable ? (
                      <div className="grid grid-cols-2 gap-2">
                        <Input
                          type="time"
                          value={overrideComposer.startTime}
                          onChange={(event) =>
                            setOverrideComposer((current) => ({
                              ...current,
                              startTime: event.target.value,
                            }))
                          }
                        />
                        <Input
                          type="time"
                          value={overrideComposer.endTime}
                          onChange={(event) =>
                            setOverrideComposer((current) => ({
                              ...current,
                              endTime: event.target.value,
                            }))
                          }
                        />
                      </div>
                    ) : null}
                    <Button
                      className="cursor-pointer"
                      onClick={handleSaveOverride}
                      disabled={upsertOverrideMutation.isPending}
                    >
                      Save exception
                    </Button>
                  </div>
                ) : null}
              </section>
            </div>
          </WorkspaceInspectorPane>
        </AppWorkspaceMainSupportingSplit>
      </AppWorkspaceTabbedPanel>
    </div>
  )
}
